import React, { useState } from 'react';
import { Award, Plus, Trash2 } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { ActionModal } from './ActionModal';
import {
  useConcorrentes,
  useDesvincularConcorrente,
  useVincularConcorrente,
} from '../hooks';
import type { OpportunityCompetitorLink } from '../api';

interface Props {
  opportunityId: string;
  canEdit: boolean;
}

const RISK_LABEL: Record<'baixo' | 'medio' | 'alto', string> = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto',
};

const RISK_STYLE: Record<'baixo' | 'medio' | 'alto', string> = {
  baixo: 'bg-green-100 text-green-800',
  medio: 'bg-amber-100 text-amber-800',
  alto: 'bg-red-100 text-red-800',
};

export const OpportunityCompetitorsSection: React.FC<Props> = ({
  opportunityId,
  canEdit,
}) => {
  const { data, isLoading, isError, error } = useConcorrentes(opportunityId);
  const detach = useDesvincularConcorrente(opportunityId);
  const [attachOpen, setAttachOpen] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<OpportunityCompetitorLink | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Concorrentes
        </h2>
        {canEdit && (
          <button
            onClick={() => setAttachOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <Plus className="h-3 w-3" />
            Vincular concorrente
          </button>
        )}
      </div>

      {rowError && (
        <Alert variant="error" className="mb-2">
          {rowError}
        </Alert>
      )}

      {isLoading && <Skeleton className="h-16 w-full" />}
      {isError && (
        <Alert variant="error">
          Erro ao carregar concorrentes:{' '}
          {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {data && data.length === 0 && (
        <p className="text-sm text-gray-500">
          Nenhum concorrente vinculado. O vencedor pode ser marcado no desfecho
          "Perdida".
        </p>
      )}

      {data && data.length > 0 && (
        <ul className="space-y-2">
          {data.map((c) => (
            <li
              key={c.id}
              className={`rounded-md border p-3 ${
                c.isWinner ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {c.competitorName ?? 'Concorrente'}
                    </span>
                    {c.isWinner && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                        <Award className="h-3 w-3" />
                        Vencedor
                      </span>
                    )}
                    {c.risk && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_STYLE[c.risk]}`}
                      >
                        Risco: {RISK_LABEL[c.risk]}
                      </span>
                    )}
                  </div>
                  {(c.solution || c.positioning) && (
                    <div className="mt-1 text-xs text-gray-600">
                      {c.solution && <div>Solução: {c.solution}</div>}
                      {c.positioning && <div>Posicionamento: {c.positioning}</div>}
                    </div>
                  )}
                  {(c.strongPoint || c.weakPoint) && (
                    <div className="mt-1 grid grid-cols-1 gap-1 text-xs md:grid-cols-2">
                      {c.strongPoint && (
                        <div className="text-green-800">
                          <strong>+</strong> {c.strongPoint}
                        </div>
                      )}
                      {c.weakPoint && (
                        <div className="text-red-800">
                          <strong>−</strong> {c.weakPoint}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {canEdit && !c.isWinner && (
                  <button
                    type="button"
                    onClick={() => {
                      setRowError(null);
                      setPendingDelete(c);
                    }}
                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Desvincular"
                    aria-label="Desvincular concorrente"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {attachOpen && (
        <AttachCompetitorModal
          opportunityId={opportunityId}
          onClose={() => setAttachOpen(false)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title="Desvincular concorrente"
          message={`Remover o vínculo com o concorrente ${pendingDelete.competitorId}?`}
          confirmLabel="Desvincular"
          danger
          busy={detach.isPending}
          onConfirm={async () => {
            try {
              await detach.mutateAsync(pendingDelete.id);
              setPendingDelete(null);
            } catch (e) {
              setRowError(getFriendlyErrorMessage(e, 'Falha ao desvincular concorrente.'));
              setPendingDelete(null);
            }
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </section>
  );
};

const AttachCompetitorModal: React.FC<{
  opportunityId: string;
  onClose: () => void;
}> = ({ opportunityId, onClose }) => {
  const [competitorName, setCompetitorName] = useState('');
  const [solution, setSolution] = useState('');
  const [positioning, setPositioning] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [strongPoint, setStrongPoint] = useState('');
  const [weakPoint, setWeakPoint] = useState('');
  const [risk, setRisk] = useState<'' | 'baixo' | 'medio' | 'alto'>('');
  const [err, setErr] = useState<string | null>(null);
  const attach = useVincularConcorrente(opportunityId);

  const submit = async () => {
    setErr(null);
    if (!competitorName.trim()) return;
    try {
      await attach.mutateAsync({
        competitorName: competitorName.trim(),
        solution: solution.trim() || undefined,
        positioning: positioning.trim() || undefined,
        priceRange: priceRange.trim() || undefined,
        strongPoint: strongPoint.trim() || undefined,
        weakPoint: weakPoint.trim() || undefined,
        risk: risk || undefined,
      });
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao vincular concorrente.'));
    }
  };

  return (
    <ActionModal
      isOpen
      onClose={onClose}
      onSubmit={submit}
      title="Vincular concorrente"
      description="O nome é usado para reaproveitar concorrentes cadastrados (comparação case-insensitive) ou criar novo sob demanda."
      submitLabel="Vincular"
      submitVariant="primary"
      isSubmitting={attach.isPending}
      submitError={err}
      canSubmit={!!competitorName.trim()}
    >
      <div className="space-y-3">
        <TextInput
          label="Nome do concorrente"
          value={competitorName}
          onChange={(e) => setCompetitorName(e.target.value)}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label="Solução ofertada (opcional)"
            value={solution}
            onChange={(e) => setSolution(e.target.value)}
          />
          <TextInput
            label="Posicionamento (opcional)"
            value={positioning}
            onChange={(e) => setPositioning(e.target.value)}
          />
          <TextInput
            label="Faixa de preço (opcional)"
            value={priceRange}
            onChange={(e) => setPriceRange(e.target.value)}
          />
          <AutocompleteSelect
            label="Risco (opcional)"
            options={[
              { value: '', label: '—' },
              { value: 'baixo', label: 'Baixo' },
              { value: 'medio', label: 'Médio' },
              { value: 'alto', label: 'Alto' },
            ]}
            value={risk}
            onChange={(v) => setRisk(v as typeof risk)}
          />
        </div>
        <TextInput
          label="Ponto forte (opcional)"
          value={strongPoint}
          onChange={(e) => setStrongPoint(e.target.value)}
        />
        <TextInput
          label="Ponto fraco (opcional)"
          value={weakPoint}
          onChange={(e) => setWeakPoint(e.target.value)}
        />
      </div>
    </ActionModal>
  );
};
